import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopNav from '../components/TopNav';
import { getExams, getMyAttempts } from '../api/exams';
import { useAuth } from '../context/AuthContext';
import { confirmCheckoutSession } from '../api/auth';
import { createDashboardT, getDashboardPlanLabel } from '../i18n/dashboard';

const CHECKOUT_RETURN_TO_KEY = 'checkout_return_to';

const styles = `
.platform-dashboard{
  min-height:100vh;
  direction:rtl;
  text-align:right;
  font-family:"Cairo","IBM Plex Sans Arabic","Inter","SF Pro Display",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;
  background:
    radial-gradient(circle at top left, rgba(99,102,241,.06), transparent 18%),
    linear-gradient(180deg, #f4f7fb 0%, #f7f9fc 100%);
}
.platform-dashboard .shell{
  max-width:1440px;
  margin:0 auto;
  padding:16px 24px 32px;
}
.platform-dashboard .sidebar{
  position:fixed;
  top:16px;
  left:16px;
  bottom:16px;
  width:min(320px, calc(100vw - 32px));
  z-index:180;
  display:flex;
  flex-direction:column;
  gap:16px;
  padding:16px;
  border-radius:28px;
  background:
    radial-gradient(circle at top right, rgba(99,102,241,.12), transparent 32%),
    linear-gradient(180deg, rgba(10,22,38,.88), rgba(12,26,44,.78));
  border:1px solid rgba(255,255,255,.08);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.05), inset -1px 0 0 rgba(255,255,255,.04), 24px 0 50px rgba(5,10,20,.12), 0 22px 42px rgba(2,8,23,.12);
  backdrop-filter:blur(18px);
  transform:translateX(calc(-100% - 20px));
  opacity:0;
  pointer-events:none;
  transition:transform .26s ease, opacity .22s ease;
}
.platform-dashboard .sidebar.open{
  transform:translateX(0);
  opacity:1;
  pointer-events:auto;
}
.platform-dashboard .drawer-backdrop{
  position:fixed;
  inset:0;
  z-index:170;
  background:rgba(3,7,18,.34);
  backdrop-filter:blur(6px);
  opacity:0;
  pointer-events:none;
  transition:opacity .24s ease;
}
.platform-dashboard .drawer-backdrop.open{
  opacity:1;
  pointer-events:auto;
}
.platform-dashboard .main{
  display:flex;
  flex-direction:column;
  gap:14px;
  width:100%;
}
.platform-dashboard .drawer-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}
.platform-dashboard .drawer-close{
  width:36px;
  height:36px;
  border:none;
  border-radius:12px;
  background:rgba(255,255,255,.06);
  color:#cbd5e1;
  cursor:pointer;
  transition:all .18s ease;
}
.platform-dashboard .drawer-close:hover{
  background:rgba(217,119,6,.14);
  color:#fdba74;
}
.platform-dashboard .sidebar-card,
.platform-dashboard .surface,
.platform-dashboard .exam-card{
  background:rgba(255,255,255,.90);
  backdrop-filter:blur(12px);
  border:1px solid rgba(148,163,184,.14);
  box-shadow:0 18px 44px rgba(15,23,42,.07);
  border-radius:14px;
}
.platform-dashboard .sidebar-card{
  padding:16px;
  background:rgba(255,255,255,.03);
  border:1px solid rgba(255,255,255,.06);
  box-shadow:inset 0 1px 0 rgba(255,255,255,.03);
}
.platform-dashboard .brand{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
}
.platform-dashboard .brand-main{
  display:flex;
  align-items:center;
  gap:12px;
  min-width:0;
}
.platform-dashboard .brand-mark{
  width:42px;
  height:42px;
  border-radius:12px;
  background:linear-gradient(135deg,var(--nv),var(--nv2));
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  font-weight:900;
  box-shadow:0 16px 28px rgba(15,23,42,.18);
}
.platform-dashboard .drawer-trigger{
  width:42px;
  height:42px;
  border:none;
  border-radius:14px;
  background:rgba(255,255,255,.92);
  color:var(--nv);
  cursor:pointer;
  box-shadow:0 14px 28px rgba(15,23,42,.08);
  transition:all .18s ease;
}
.platform-dashboard .drawer-trigger:hover{
  background:#fff7ed;
  color:#b45309;
  transform:translateY(-1px);
}
.platform-dashboard .eyebrow{
  font-size:10px;
  letter-spacing:.16em;
  text-transform:uppercase;
  color:#94a3b8;
  font-weight:800;
}
.platform-dashboard .brand h2{
  margin:4px 0 0;
  font-size:15px;
  color:#f8fafc;
}
.platform-dashboard .nav-list{
  display:flex;
  flex-direction:column;
  gap:10px;
}
.platform-dashboard .nav-item{
  position:relative;
  width:100%;
  border:1px solid rgba(255,255,255,.04);
  background:rgba(255,255,255,.03);
  border-radius:14px;
  padding:12px 14px;
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px;
  cursor:pointer;
  overflow:hidden;
  transition:transform .18s ease, border-color .18s ease, box-shadow .18s ease, background .18s ease, color .18s ease;
}
.platform-dashboard .nav-item::before{
  content:'';
  position:absolute;
  left:0;
  top:10px;
  bottom:10px;
  width:3px;
  border-radius:999px;
  background:transparent;
  transition:background .18s ease, opacity .18s ease;
  opacity:0;
}
.platform-dashboard .nav-item:hover{
  transform:translateX(3px);
  background:linear-gradient(135deg, rgba(255,255,255,.06), rgba(99,102,241,.08));
  border-color:rgba(129,140,248,.18);
  box-shadow:0 14px 26px rgba(2,8,23,.18);
}
.platform-dashboard .nav-item.active{
  background:linear-gradient(135deg, rgba(217,119,6,.18), rgba(99,102,241,.10));
  border-color:rgba(251,191,36,.18);
  box-shadow:inset 0 0 0 1px rgba(251,191,36,.08), 0 16px 32px rgba(217,119,6,.12);
}
.platform-dashboard .nav-item.active::before{
  background:var(--ac);
  opacity:1;
}
.platform-dashboard .nav-main{
  display:flex;
  align-items:center;
  gap:12px;
  color:rgba(248,250,252,.86);
  font-size:14px;
  font-weight:700;
}
.platform-dashboard .nav-icon{
  width:36px;
  height:36px;
  border-radius:10px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:rgba(255,255,255,.05);
  color:#cbd5e1;
  font-size:17px;
  transition:transform .18s ease, background .18s ease, color .18s ease;
}
.platform-dashboard .nav-item:hover .nav-icon{
  transform:scale(1.05);
  color:#c4b5fd;
  background:rgba(99,102,241,.14);
}
.platform-dashboard .nav-item.active .nav-icon{
  background:rgba(217,119,6,.14);
  color:#fdba74;
}
.platform-dashboard .nav-item.active .nav-main,
.platform-dashboard .nav-item.active .nav-meta{
  color:#fdba74;
}
.platform-dashboard .nav-meta{
  font-size:10px;
  color:rgba(148,163,184,.66);
  font-weight:800;
  letter-spacing:.1em;
  text-transform:uppercase;
}
.platform-dashboard .sidebar-plan{
  background:
    radial-gradient(circle at top right, rgba(217,119,6,.16), transparent 34%),
    radial-gradient(circle at 20% 10%, rgba(99,102,241,.16), transparent 28%),
    linear-gradient(180deg,var(--nv),var(--nv2));
  color:#fff;
  box-shadow:0 18px 36px rgba(2,8,23,.24);
}
.platform-dashboard .sidebar-plan .eyebrow{
  color:rgba(255,255,255,.52);
}
.platform-dashboard .sidebar-plan-title{
  margin:8px 0 6px;
  font-size:18px;
  font-weight:900;
}
.platform-dashboard .sidebar-plan-copy{
  color:rgba(255,255,255,.7);
  font-size:13px;
  line-height:1.7;
}
.platform-dashboard .sidebar-plan-cta{
  margin-top:16px;
  width:100%;
  border:none;
  border-radius:14px;
  background:linear-gradient(135deg,var(--ac),#d97706);
  color:#fff;
  font-size:14px;
  font-weight:800;
  padding:13px 14px;
  cursor:pointer;
  transition:transform .18s ease, box-shadow .18s ease, filter .18s ease;
}
.platform-dashboard .sidebar-plan-cta:hover{
  transform:translateY(-1px);
  filter:brightness(1.02);
  box-shadow:0 0 0 1px rgba(251,191,36,.08), 0 16px 28px rgba(217,119,6,.24);
}
.platform-dashboard .surface{
  padding:18px;
  background:linear-gradient(180deg, rgba(255,255,255,.92), rgba(248,250,252,.92));
  box-shadow:0 20px 48px rgba(15,23,42,.08);
}
.platform-dashboard .workspace-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:12px;
  flex-wrap:wrap;
}
.platform-dashboard .workspace-title{
  margin:0;
  font-size:32px;
  line-height:1.05;
  color:#0f172a;
  letter-spacing:-.03em;
  font-weight:800;
}
.platform-dashboard .workspace-copy{
  margin:6px 0 0;
  max-width:520px;
  color:#64748b;
  font-size:13px;
  line-height:1.6;
}
.platform-dashboard .workspace-actions{
  display:flex;
  align-items:center;
  gap:10px;
  flex-wrap:wrap;
}
.platform-dashboard .subtle-cta,
.platform-dashboard .primary-cta,
.platform-dashboard .ghost-cta{
  border:none;
  border-radius:14px;
  padding:12px 16px;
  font-size:14px;
  font-weight:800;
  cursor:pointer;
  transition:transform .18s ease, box-shadow .18s ease, background .18s ease, border-color .18s ease, color .18s ease;
}
.platform-dashboard .subtle-cta{
  background:rgba(99,102,241,.08);
  color:#4f46e5;
}
.platform-dashboard .primary-cta{
  background:linear-gradient(135deg,var(--ac),#d97706);
  color:#fff;
  box-shadow:0 14px 28px rgba(217,119,6,.18);
}
.platform-dashboard .ghost-cta{
  background:rgba(255,255,255,.88);
  color:#334155;
  border:1px solid rgba(148,163,184,.18);
}
.platform-dashboard .subtle-cta:hover,
.platform-dashboard .primary-cta:hover,
.platform-dashboard .ghost-cta:hover{
  transform:translateY(-1px);
}
.platform-dashboard .primary-cta:hover{
  box-shadow:0 18px 34px rgba(217,119,6,.24);
}
.platform-dashboard .primary-cta:focus,
.platform-dashboard .subtle-cta:focus,
.platform-dashboard .ghost-cta:focus,
.platform-dashboard .drawer-trigger:focus,
.platform-dashboard .drawer-close:focus,
.platform-dashboard .nav-item:focus{
  outline:none;
  box-shadow:0 0 0 4px rgba(251,191,36,.16);
}
.platform-dashboard .primary-cta:active,
.platform-dashboard .subtle-cta:active,
.platform-dashboard .ghost-cta:active{
  transform:translateY(0);
}
.platform-dashboard .section-head{
  display:flex;
  justify-content:space-between;
  align-items:flex-end;
  gap:10px;
  flex-wrap:wrap;
  margin-bottom:14px;
}
.platform-dashboard .section-title{
  margin:0;
  font-size:26px;
  color:#0f172a;
  font-weight:900;
  letter-spacing:-.02em;
}
.platform-dashboard .page-heading{
  margin:0;
  font-size:34px;
  line-height:1.08;
  letter-spacing:-.03em;
  color:#0f172a;
  font-weight:900;
}
.platform-dashboard .page-copy{
  margin:10px 0 0;
  max-width:640px;
  color:#64748b;
  font-size:14px;
  line-height:1.7;
}
.platform-dashboard .section-copy{
  margin:6px 0 0;
  color:#475569;
  font-size:15px;
  line-height:1.75;
}
.platform-dashboard .stats-grid{
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:14px;
}
.platform-dashboard .stat-card{
  padding:18px;
  border-radius:16px;
  border:1px solid rgba(226,232,240,.92);
  background:linear-gradient(180deg,#ffffff,#f8fbff);
  box-shadow:0 18px 34px rgba(15,23,42,.06);
  transition:transform .18s ease, box-shadow .18s ease, border-color .18s ease;
}
.platform-dashboard .stat-card:hover{
  transform:translateY(-2px);
  box-shadow:0 22px 42px rgba(15,23,42,.08);
}
.platform-dashboard .stat-top{
  display:flex;
  justify-content:space-between;
  align-items:center;
  margin-bottom:12px;
}
.platform-dashboard .stat-icon{
  width:48px;
  height:48px;
  border-radius:14px;
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:20px;
}
.platform-dashboard .stat-card.blue{
  background:linear-gradient(180deg,#fff7ed,#ffffff);
  border-color:rgba(251,146,60,.22);
}
.platform-dashboard .stat-card.violet{
  background:linear-gradient(180deg,#f5f3ff,#ffffff);
  border-color:rgba(129,140,248,.22);
}
.platform-dashboard .stat-card.emerald{
  background:linear-gradient(180deg,#ecfdf5,#ffffff);
  border-color:rgba(16,185,129,.18);
}
.platform-dashboard .stat-card.blue .stat-icon{background:rgba(249,115,22,.12);color:#c2410c}
.platform-dashboard .stat-card.violet .stat-icon{background:rgba(99,102,241,.10);color:#4f46e5}
.platform-dashboard .stat-card.emerald .stat-icon{background:rgba(16,185,129,.12);color:#047857}
.platform-dashboard .stat-label{
  font-size:13px;
  text-transform:uppercase;
  letter-spacing:.1em;
  color:#475569;
  font-weight:800;
}
.platform-dashboard .stat-value{
  margin-top:6px;
  color:#0f172a;
  font-size:42px;
  line-height:1;
  font-weight:900;
}
.platform-dashboard .stat-note{
  margin-top:10px;
  color:#475569;
  font-size:14px;
  line-height:1.8;
}
.platform-dashboard .plan-grid{
  display:grid;
  grid-template-columns:minmax(0,1.3fr) minmax(320px,.9fr);
  gap:12px;
}
.platform-dashboard .plan-card{
  padding:18px;
  border-radius:16px;
  background:linear-gradient(180deg,#ffffff,#f8fbff);
  border:1px solid rgba(226,232,240,.92);
  box-shadow:0 18px 34px rgba(15,23,42,.06);
}
.platform-dashboard .plan-badge{
  display:inline-flex;
  align-items:center;
  gap:8px;
  border-radius:999px;
  padding:7px 12px;
  font-size:11px;
  font-weight:900;
  letter-spacing:.08em;
  text-transform:uppercase;
}
.platform-dashboard .plan-badge.ai{background:#dcfce7;color:#047857}
.platform-dashboard .plan-badge.basic{background:rgba(99,102,241,.10);color:#4f46e5}
.platform-dashboard .plan-badge.promo{background:#ffedd5;color:#c2410c}
.platform-dashboard .plan-badge.free{background:#e2e8f0;color:#334155}
.platform-dashboard .plan-title{
  margin:14px 0 8px;
  font-size:28px;
  line-height:1.15;
  color:#0f172a;
  font-weight:900;
}
.platform-dashboard .plan-copy{
  color:#475569;
  font-size:16px;
  line-height:1.86;
  margin:0;
}
.platform-dashboard .credit-meter{
  margin-top:16px;
  padding:14px;
  border-radius:14px;
  background:linear-gradient(180deg,#f8fafc,#f1f5f9);
  border:1px solid rgba(226,232,240,.9);
}
.platform-dashboard .credit-meta{
  display:flex;
  justify-content:space-between;
  gap:10px;
  align-items:center;
  flex-wrap:wrap;
}
.platform-dashboard .credit-meta strong{
  color:#0f172a;
  font-size:20px;
}
.platform-dashboard .credit-meta span{
  color:#475569;
  font-size:14px;
  font-weight:700;
}
.platform-dashboard .credit-number{
  margin-top:4px;
  color:#0f172a;
  font-size:40px;
  font-weight:800;
  line-height:1;
}
.platform-dashboard .credit-support{
  margin-top:6px;
  color:#64748b;
  font-size:14px;
  font-weight:700;
}
.platform-dashboard .credit-status{
  margin-top:10px;
  display:inline-flex;
  align-items:center;
  gap:8px;
  border-radius:999px;
  padding:7px 10px;
  font-size:13px;
  font-weight:800;
  background:rgba(15,23,42,.04);
  color:#64748b;
}
.platform-dashboard .credit-status.low{
  background:rgba(217,119,6,.10);
  color:#b45309;
}
.platform-dashboard .progress-track{
  margin-top:12px;
  width:100%;
  height:10px;
  border-radius:999px;
  background:#e2e8f0;
  overflow:hidden;
}
.platform-dashboard .progress-fill{
  height:100%;
  border-radius:999px;
  background:linear-gradient(135deg,var(--ac),#d97706);
  width:0;
  transition:width .55s cubic-bezier(.2,.8,.2,1);
}
.platform-dashboard .progress-fill.low{
  background:linear-gradient(135deg,#f59e0b,#ea580c);
}
.platform-dashboard .upgrade-card{
  padding:18px;
  border-radius:16px;
  background:
    radial-gradient(circle at top right, rgba(217,119,6,.14), transparent 34%),
    radial-gradient(circle at 20% 10%, rgba(99,102,241,.14), transparent 28%),
    linear-gradient(180deg,#152235,#0f172a);
  color:#fff;
  border:1px solid rgba(255,255,255,.06);
  box-shadow:0 18px 36px rgba(15,23,42,.14);
}
.platform-dashboard .upgrade-card h3{
  margin:10px 0 6px;
  font-size:26px;
}
.platform-dashboard .upgrade-card p{
  margin:0;
  color:rgba(255,255,255,.7);
  font-size:16px;
  line-height:1.82;
}
.platform-dashboard .upgrade-list{
  margin:16px 0 0;
  padding:0;
  list-style:none;
  display:grid;
  gap:10px;
}
.platform-dashboard .upgrade-list li{
  display:flex;
  align-items:flex-start;
  gap:10px;
  font-size:15px;
  color:#e2e8f0;
  line-height:1.75;
}
.platform-dashboard .upgrade-dot{
  width:8px;
  height:8px;
  border-radius:999px;
  background:var(--ac);
  margin-top:6px;
  flex:0 0 auto;
}
.platform-dashboard .exams-section{
  position:relative;
}
.platform-dashboard .exams-topbar{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
}
.platform-dashboard .exams-count{
  display:inline-flex;
  align-items:center;
  gap:8px;
  padding:6px 12px;
  border-radius:999px;
  background:rgba(217,119,6,.08);
  color:#b45309;
  font-size:14px;
  font-weight:800;
}
.platform-dashboard .exams-grid{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(320px,1fr));
  gap:16px;
  animation:examPageFade .24s ease;
  align-items:stretch;
}
.platform-dashboard .exam-card{
  overflow:visible;
  transition:transform .2s ease, box-shadow .2s ease, border-color .2s ease;
  cursor:pointer;
  min-height:372px;
  height:auto;
  display:flex;
  flex-direction:column;
  border-color:rgba(148,163,184,.18);
}
.platform-dashboard .exam-card:hover{
  transform:translateY(-5px);
  box-shadow:0 32px 64px rgba(15,23,42,.16);
  border-color:rgba(217,119,6,.3);
}
.platform-dashboard .exam-card-head{
  padding:24px;
  background:
    radial-gradient(circle at top right, rgba(245,158,11,.12), transparent 30%),
    linear-gradient(135deg,var(--nv),var(--nv2));
  border-bottom:1px solid rgba(15,23,42,.08);
  direction:ltr;
  text-align:left;
}
.platform-dashboard .exam-icon{
  width:52px;
  height:52px;
  border-radius:14px;
  background:rgba(255,255,255,.12);
  color:var(--ac);
  display:flex;
  align-items:center;
  justify-content:center;
  font-size:20px;
  box-shadow:inset 0 1px 0 rgba(255,255,255,.5);
}
.platform-dashboard .exam-title{
  margin:16px 0 8px;
  color:#fff;
  font-size:22px;
  font-weight:900;
  line-height:1.35;
}
.platform-dashboard .exam-description{
  color:rgba(255,255,255,.78);
  font-size:15px;
  line-height:1.75;
}
.platform-dashboard .exam-meta{
  padding:22px 24px 24px;
  display:flex;
  flex-direction:column;
  justify-content:flex-start;
  gap:22px;
  flex:1;
  direction:ltr;
  text-align:left;
}
.platform-dashboard .tag-row{
  display:flex;
  gap:8px;
  flex-wrap:wrap;
  margin-bottom:18px;
}
.platform-dashboard .skill-tag{
  display:inline-flex;
  align-items:center;
  gap:6px;
  padding:8px 12px;
  border-radius:999px;
  font-size:12px;
  font-weight:800;
  border:1px solid rgba(203,213,225,.72);
  background:#f8fafc;
  color:#334155;
  transition:all .18s ease;
}
.platform-dashboard .exam-card:hover .skill-tag{
  border-color:rgba(217,119,6,.14);
  color:#334155;
}
.platform-dashboard .exam-footer{
  display:flex;
  justify-content:space-between;
  align-items:center;
  gap:12px;
  flex-wrap:wrap;
  margin-top:auto;
}
.platform-dashboard .exam-time{
  color:#475569;
  font-size:14px;
  font-weight:700;
}
.platform-dashboard .open-button{
  min-width:190px;
  min-height:52px;
  padding:0 22px;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  font-size:16px;
  font-weight:900;
  line-height:1.1;
  white-space:nowrap;
  direction:rtl;
  box-shadow:0 14px 24px rgba(217,119,6,.18);
}
.platform-dashboard .exam-card:hover .open-button{
  transform:translateY(-1px) scale(1.02);
  filter:brightness(1.03);
  box-shadow:0 18px 30px rgba(217,119,6,.26);
}
.platform-dashboard .empty-state,
.platform-dashboard .loading-grid{
  border:1px dashed rgba(148,163,184,.35);
  background:rgba(255,255,255,.82);
  border-radius:20px;
  padding:24px;
}
.platform-dashboard .empty-state{
  text-align:center;
}
.platform-dashboard .empty-state h3{
  margin:12px 0 6px;
  color:#0f172a;
}
.platform-dashboard .empty-state p{
  margin:0;
  color:#475569;
  font-size:15px;
  line-height:1.8;
}
.platform-dashboard .loading-grid{
  display:grid;
  grid-template-columns:repeat(auto-fit,minmax(320px,1fr));
  gap:18px;
}
.platform-dashboard .skeleton-card{
  height:320px;
  border-radius:18px;
  background:linear-gradient(90deg,#eef2f7 25%,#f8fafc 37%,#eef2f7 63%);
  background-size:400% 100%;
  animation:dashboardShimmer 1.4s ease infinite;
}
@keyframes examPageFade{
  from{opacity:.35;transform:translateY(6px)}
  to{opacity:1;transform:translateY(0)}
}
.platform-dashboard .pagination{
  margin-top:18px;
  display:flex;
  align-items:center;
  justify-content:center;
  gap:8px;
  flex-wrap:wrap;
  direction:ltr;
}
.platform-dashboard .page-btn{
  min-width:40px;
  height:40px;
  border:none;
  border-radius:12px;
  background:#fff;
  color:#475569;
  border:1px solid rgba(203,213,225,.9);
  font-size:14px;
  font-weight:800;
  box-shadow:0 10px 20px rgba(15,23,42,.04);
  transition:all .18s ease;
}
.platform-dashboard .page-btn:hover:not(:disabled){
  transform:translateY(-1px);
  border-color:rgba(217,119,6,.24);
  color:#b45309;
}
.platform-dashboard .page-btn.active{
  background:linear-gradient(135deg,var(--ac),#d97706);
  color:#fff;
  border-color:transparent;
  box-shadow:0 14px 26px rgba(217,119,6,.24);
}
.platform-dashboard .page-btn.wide{
  min-width:92px;
  padding:0 14px;
}
.platform-dashboard .page-btn:disabled{
  opacity:.45;
  cursor:not-allowed;
  box-shadow:none;
}
@keyframes dashboardShimmer{
  0%{background-position:100% 0}
  100%{background-position:0 0}
}
@media (max-width:1100px){
  .platform-dashboard .plan-grid{grid-template-columns:1fr}
}
@media (max-width:760px){
  .platform-dashboard .shell{padding:18px 14px 28px}
  .platform-dashboard .stats-grid{grid-template-columns:1fr}
  .platform-dashboard .workspace-title{font-size:28px}
  .platform-dashboard .page-heading{font-size:28px}
  .platform-dashboard .section-title{font-size:22px}
  .platform-dashboard .stat-value{font-size:36px}
  .platform-dashboard .sidebar{
    top:10px;
    left:10px;
    bottom:10px;
    width:calc(100vw - 20px);
    padding:14px;
  }
  .platform-dashboard .exams-grid,
  .platform-dashboard .loading-grid{
    grid-template-columns:1fr;
  }
  .platform-dashboard .exam-card{
    height:auto;
    min-height:0;
  }
}
`;

export default function Platform() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const t = createDashboardT('ar');
  const [exams, setExams] = useState([]);
  const [attempts, setAttempts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [progressReady, setProgressReady] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [checkoutReturnTo, setCheckoutReturnTo] = useState('');
  // Server-side pagination — the dashboard now asks the backend for one
  // page of EXAMS_PER_PAGE exams at a time instead of pulling the full
  // catalogue (which got slow once the library passed ~50 exams). Pages
  // are cached client-side so navigating back to a page already seen is
  // instant.
  const EXAMS_PER_PAGE = 5;
  const [totalExamsCount, setTotalExamsCount] = useState(0);
  const [examsHasNext, setExamsHasNext] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  // Active skill tab. 'all' | 'writing' | 'reading' | 'speaking'.
  // The backend filters by `primary_skill` so each page is consistent
  // within a tab (page 2 of "Reading" returns more reading exams,
  // not whatever exam happens to be next in the global order).
  const [activeSkill, setActiveSkill] = useState('all');
  // Cache keyed by `${skill}:${page}` so tab switches and pagination
  // both reuse previously fetched pages — repeat clicks are instant.
  const examsCacheRef = useRef(new Map());
  const formatAttemptDate = (value) => {
    if (!value) return 'آخر محاولة غير مكتملة';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'تاريخ غير متاح';
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Single-page fetch with a per-page cache. The backend returns
  // either an array (legacy) or {results, count, has_next, ...} when
  // `page` is included — we handle both shapes so this keeps working
  // even if the server is rolled back.
  const loadExamsPage = useCallback(async (page, skill) => {
    const nextSkill = skill === undefined ? activeSkill : skill;
    const cacheKey = `${nextSkill}:${page}`;
    const cached = examsCacheRef.current.get(cacheKey);
    if (cached) {
      setExams(cached.results);
      if (cached.totalCount !== undefined) setTotalExamsCount(cached.totalCount);
      if (cached.hasNext !== undefined) setExamsHasNext(cached.hasNext);
      setCurrentPage(page);
      setActiveSkill(nextSkill);
      return;
    }
    setPageLoading(true);
    try {
      const params = { family: 'general', page, page_size: EXAMS_PER_PAGE };
      if (nextSkill && nextSkill !== 'all') params.primary_skill = nextSkill;
      const data = await getExams(params);
      const isPaged = data && !Array.isArray(data) && Array.isArray(data.results);
      const results = isPaged ? data.results : (Array.isArray(data) ? data : []);
      const totalCount = isPaged ? (Number(data.count) || 0) : results.length;
      const hasNext = isPaged ? Boolean(data.has_next) : false;
      examsCacheRef.current.set(cacheKey, { results, totalCount, hasNext });
      setExams(results);
      setTotalExamsCount(totalCount);
      setExamsHasNext(hasNext);
      setCurrentPage(page);
      setActiveSkill(nextSkill);
    } finally {
      setPageLoading(false);
    }
  }, [activeSkill]);

  const didInitialLoadRef = useRef(false);
  useEffect(() => {
    if (didInitialLoadRef.current) return;
    didInitialLoadRef.current = true;
    // Kick off both calls in parallel so attempts don't wait for exams.
    Promise.all([
      loadExamsPage(1, 'all').catch((err) => { console.error(err); }),
      getMyAttempts().then(setAttempts).catch(() => setAttempts([])),
    ]).finally(() => setLoading(false));
  }, [loadExamsPage]);

  useEffect(() => {
    const timeout = setTimeout(() => setProgressReady(true), 120);
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    const checkoutState = searchParams.get('checkout');
    const sessionId = searchParams.get('session_id');
    const storedReturnTo = typeof window !== 'undefined' ? window.sessionStorage.getItem(CHECKOUT_RETURN_TO_KEY) || '' : '';
    const returnTo = searchParams.get('return_to') || storedReturnTo || '/platform';
    if (checkoutState !== 'success' || !sessionId || !user) return;

    let ignore = false;
    const run = async () => {
      try {
        const result = await confirmCheckoutSession(sessionId);
        if (ignore) return;
        if (result?.status === 'completed') {
          await refreshUser().catch(() => {});
          setCheckoutMessage('تم تأكيد عملية الدفع بنجاح، وتم تحديث الباقة أو الرصيد على حسابك.');
          setCheckoutReturnTo(returnTo && returnTo !== '/platform' ? returnTo : '');
        } else {
          setCheckoutMessage('تم استلام عملية الدفع، ونعمل الآن على تحديث حسابك.');
        }

      } catch (error) {
        if (!ignore) {
          setCheckoutMessage(error?.response?.data?.error || 'تم الدفع لكن تأكيد العملية ما زال قيد المعالجة. أعد تحميل الصفحة بعد لحظات.');
        }
      } finally {
        if (!ignore) {
          window.sessionStorage.removeItem(CHECKOUT_RETURN_TO_KEY);
          setSearchParams({}, { replace: true });
        }
      }
    };

    run();
    return () => { ignore = true; };
  }, [searchParams, setSearchParams, user, refreshUser, navigate]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(totalExamsCount / EXAMS_PER_PAGE));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalExamsCount, currentPage]);

  const planName = getDashboardPlanLabel(user?.plan, t);
  const creditBaseline = useMemo(() => {
    if (user?.plan === 'ai') return Math.max(user?.ai_credits || 0, 10);
    if (user?.plan === 'promo') return Math.max(user?.ai_credits || 0, 2);
    return Math.max(user?.ai_credits || 0, 1);
  }, [user]);
  const creditProgress = Math.min(100, Math.round(((user?.ai_credits || 0) / creditBaseline) * 100));
  const lowCredits = user?.plan === 'ai' ? (user?.ai_credits || 0) <= Math.max(2, Math.ceil(creditBaseline * 0.2)) : user?.plan === 'promo' ? (user?.ai_credits || 0) <= 1 : false;
  const latestAttempt = attempts[0];
  const startAction = latestAttempt
    ? { label: 'متابعة آخر جلسة', onClick: () => navigate('/results') }
    : exams[0]
      ? { label: 'ابدأ الاختبار', onClick: () => navigate(`/hub/${exams[0].id}`) }
      : { label: 'إدارة الخطة', onClick: () => navigate('/pricing') };
  const nextAction = user?.plan === 'ai'
    ? {
        title: 'ابدأ اختباراً كاملاً',
        copy: 'لديك كل الأدوات المتقدمة. ابدأ محاكاة كاملة الآن وراجع التقييم الذكي بعد الانتهاء.',
        cta: startAction.label,
        onClick: startAction.onClick,
      }
    : user?.plan === 'basic'
      ? {
          title: 'رقِّ للحصول على تصحيح ذكي',
          copy: 'يمكنك الآن التدرب على القراءة والكتابة، ثم الترقية عندما تحتاج إلى تقييم ذكي واختبار Speaking ومحاكاة كاملة.',
          cta: 'راجع الخطط',
          onClick: () => navigate('/pricing'),
        }
      : {
          title: 'اختر الخطة المناسبة للبدء',
          copy: 'فعّل التدريب المنظّم والتقييم الذكي ومحاكاة الاختبار عبر خطة واحدة واضحة.',
          cta: 'إدارة الخطة',
          onClick: () => navigate('/pricing'),
        };

  const stats = [
    {
      label: 'الاختبارات المكتملة',
      value: attempts.length,
      note: latestAttempt ? `آخر اختبار: ${latestAttempt.exam_title}` : 'أكمل أول اختبار لعرض تقدمك بشكل أوضح.',
      tone: 'blue',
      icon: '✓',
    },
    {
      label: 'المستوى المستهدف',
      value: 'B1',
      note: 'المحتوى الحالي مصمم ليتماشى مع تحضير الكتابة والتحدث والقراءة لمستوى B1.',
      tone: 'violet',
      icon: '◎',
    },
    {
      label: 'رصيد الذكاء الاصطناعي',
      value: user?.ai_credits ?? 0,
      note: user?.ai_credits > 0 ? 'الرصيد جاهز للتصحيح الذكي واختبار Speaking والمحاكاة الكاملة.' : 'قم بالترقية عندما تحتاج إلى تقييم ذكي وجلسات Speaking.',
      tone: 'emerald',
      icon: '◈',
    },
  ];
  const totalPages = Math.max(1, Math.ceil(totalExamsCount / EXAMS_PER_PAGE));
  // `exams` is already the current page's slice — the backend returned
  // exactly the rows for this page, so we render the array as-is.
  const paginatedExams = exams;
  return (
    <div className="platform-dashboard">
      <style>{styles}</style>
      <TopNav
        backTo="/landing"
        backLabel="الرئيسية"
        title={t('dashboard')}
      />
      <div className="shell">
          {checkoutMessage && (
            <div style={{ marginBottom: 14, background: 'linear-gradient(135deg, #ecfdf5, #f0fdf4)', border: '1px solid #86efac', color: '#065f46', borderRadius: 18, padding: '16px 18px', fontWeight: 700, boxShadow: '0 16px 36px rgba(16,185,129,.10)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div>{checkoutMessage}</div>
              {checkoutReturnTo && (
                <button
                  type="button"
                  onClick={() => navigate(checkoutReturnTo)}
                  style={{ height: 42, padding: '0 16px', borderRadius: 12, border: 'none', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', fontWeight: 900, cursor: 'pointer', boxShadow: '0 12px 28px rgba(5,150,105,.18)' }}
                >
                  متابعة
                </button>
              )}
            </div>
          )}
          <main className="main">
            <section className="surface">
              <div className="section-head">
                <div>
                </div>
              </div>
              <div className="stats-grid">
                {stats.map((stat) => (
                  <div key={stat.label} className={`stat-card ${stat.tone}`}>
                    <div className="stat-top">
                      <span className="stat-label">{stat.label}</span>
                      <div className="stat-icon">{stat.icon}</div>
                    </div>
                    <div className="stat-value">{stat.value}</div>
                    <div className="stat-note">{stat.note}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="surface exams-section" id="platform-exams">
              <div className="exams-topbar">
                <div className="section-head">
                  <div>
                    <h2 className="section-title">الاختبارات</h2>
                    <p className="section-copy">ابدأ الاختبار التالي مباشرة من أول شاشة داخل لوحة التحكم.</p>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span className="exams-count">{totalExamsCount} اختبار</span>
                  <button className="ghost-cta" onClick={() => navigate('/pricing')}>إدارة الخطة</button>
                </div>
              </div>

              {/* Skill tabs — server-side filtered. Clicking re-fetches
                  page 1 of that skill, so each tab is a clean view of
                  just-that-skill exams. Matches the FET dashboard pattern.
                  Reading is RTL so we lay tabs out as flex-wrap. */}
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
                {[
                  { key: 'all', label: 'الكل' },
                  { key: 'writing', label: '✍ كتابة' },
                  { key: 'reading', label: '📖 قراءة' },
                  { key: 'speaking', label: '🎙 محادثة' },
                ].map((tab) => {
                  const active = activeSkill === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => loadExamsPage(1, tab.key)}
                      disabled={pageLoading}
                      style={{
                        padding: '8px 16px',
                        borderRadius: 999,
                        border: active ? '1px solid #4f46e5' : '1px solid #e2e8f0',
                        background: active ? '#4f46e5' : '#fff',
                        color: active ? '#fff' : '#475569',
                        fontWeight: 800,
                        fontSize: 13,
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {loading ? (
                <div className="loading-grid">
                  {[1, 2, 3].map((item) => <div key={item} className="skeleton-card" />)}
                </div>
              ) : totalExamsCount === 0 ? (
                <div className="empty-state">
                  <div style={{ fontSize: 40, opacity: .5 }}>◎</div>
                  <h3>لا توجد اختبارات منشورة بعد</h3>
                  <p>
                    عند إضافة الاختبارات ستظهر هنا مع إجراءات واضحة حتى يتمكن المتعلم من البدء فوراً. يمكن للمشرف نشر المحتوى من لوحة الإدارة.
                  </p>
                  {user?.is_admin && (
                    <button className="primary-cta" style={{ marginTop: 18 }} onClick={() => navigate('/admin')}>
                      فتح لوحة الإدارة
                    </button>
                  )}
                </div>
              ) : (
                <>
                <div className="exams-grid" key={`${activeSkill}:${currentPage}`} style={{ opacity: pageLoading ? 0.55 : 1, transition: 'opacity .15s ease' }}>
                  {paginatedExams.map((exam) => {
                    // Skip the /hub/:id intermediate page — go straight
                    // into the exam's primary skill. Card UI also only
                    // shows the relevant skill tag (not all three) so
                    // it doesn't lie about what content this exam has.
                    const SKILL_ROUTES = { writing: 'writing', reading: 'reading', speaking: 'speaking' };
                    const SKILL_TAGS = {
                      writing: { cls: 'writing', label: '✍ Writing' },
                      reading: { cls: 'reading', label: '📖 Reading' },
                      speaking: { cls: 'speaking', label: '🎙 Speaking' },
                    };
                    const skill = SKILL_ROUTES[exam.primary_skill] ? exam.primary_skill : 'writing';
                    const target = `/${skill}/${exam.id}`;
                    const tag = SKILL_TAGS[skill];
                    return (
                      <article key={exam.id} className="exam-card" onClick={() => navigate(target)}>
                        <div className="exam-card-head">
                          <div className="exam-icon">✦</div>
                          <h3 className="exam-title">{exam.title}</h3>
                          <div className="exam-description">{exam.description || 'Structured B1 exam practice.'}</div>
                        </div>
                        <div className="exam-meta">
                          <div className="tag-row">
                            <span className={`skill-tag ${tag.cls}`}>{tag.label}</span>
                          </div>
                          <div className="exam-footer">
                            <span className="exam-time">⏱ {exam.time_mins} min</span>
                            <button className="primary-cta open-button" onClick={(event) => { event.stopPropagation(); navigate(target); }}>
                              ابدأ الاختبار
                            </button>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
                {totalPages > 1 && (
                  <div className="pagination">
                    <button
                      className="page-btn wide"
                      onClick={() => loadExamsPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1 || pageLoading}
                    >السابق</button>
                    {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                      <button
                        key={page}
                        className={`page-btn${page === currentPage ? ' active' : ''}`}
                        onClick={() => loadExamsPage(page)}
                        disabled={pageLoading}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      className="page-btn wide"
                      onClick={() => loadExamsPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages || pageLoading}
                    >التالي</button>
                  </div>
                )}
                </>
              )}
            </section>

            <section className="surface">
              <div className="section-head">
                <div>
                  <h2 className="section-title">التقارير الأخيرة</h2>
                  <p className="section-copy">آخر المحاولات تبقى ظاهرة هنا حتى بعد تقليل الاختبارات المتاحة إلى اختبار واحد فقط.</p>
                </div>
              </div>

              {attempts.length === 0 ? (
                <div className="empty-state" style={{ minHeight: 220 }}>
                  <div style={{ fontSize: 40, opacity: .5 }}>🗂</div>
                  <h3>لا توجد تقارير بعد</h3>
                  <p>أكمل أول محاولة وسيظهر سجل نتائجك هنا بشكل مختصر وسهل الوصول.</p>
                </div>
              ) : (
                <div style={{ display: 'grid', gap: 14 }}>
                  {attempts.slice(0, 5).map((attempt) => (
                    <button
                      key={attempt.id}
                      type="button"
                      onClick={() => navigate('/results')}
                      style={{
                        width: '100%',
                        textAlign: 'right',
                        border: '1px solid rgba(226,232,240,.92)',
                        borderRadius: 18,
                        background: 'linear-gradient(180deg,rgba(255,255,255,.98),rgba(249,250,251,.96))',
                        padding: '16px 18px',
                        cursor: 'pointer',
                        boxShadow: '0 16px 32px rgba(15,23,42,.05)',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{attempt.exam_title}</div>
                          <div style={{ fontSize: 13, color: '#64748b' }}>{formatAttemptDate(attempt.completed_at || attempt.started_at)}</div>
                        </div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', padding: '8px 12px', borderRadius: 999, background: attempt.status === 'completed' ? '#dcfce7' : '#eef2ff', color: attempt.status === 'completed' ? '#166534' : '#4338ca', fontSize: 12, fontWeight: 800 }}>
                          {attempt.status === 'completed' ? 'مكتمل' : 'قيد المعالجة'}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="surface">
              <div className="section-head">
                <div>
                  <h2 className="section-title">حالة الخطة</h2>
                  <p className="section-copy">حالة الوصول والرصيد الذكي والخطوة التالية تبقى متاحة أسفل معاينة الاختبارات.</p>
                </div>
              </div>
              <div className="plan-grid">
                <div className="plan-card">
                  <span className={`plan-badge ${user?.plan || 'free'}`}>{planName}</span>
                  <h3 className="plan-title">لوحة واضحة بدون ازدحام</h3>
                  <p className="plan-copy">
                    {user?.plan === 'ai'
                      ? 'مساحة العمل جاهزة الآن للتدريب المتقدم. واصل استخدام الرصيد في التصحيح الذكي وجلسات Speaking والمحاكاة الواقعية.'
                      : user?.plan === 'basic'
                        ? 'لديك بالفعل تدريب منظم. الترقية التالية ستفتح لك التصحيح الذكي وتقييم Speaking وتجربة الاختبار الكامل.'
                        : user?.plan === 'promo'
                          ? 'الخطة التجريبية تتيح لك تجربة النظام بالكامل مرة واحدة. استخدم رصيدك وجرّب مزايا الذكاء الاصطناعي ثم قم بالترقية عندما تكون جاهزاً.'
                          : 'اختر خطة واحدة لتفعيل التحضير المتقدم للاختبار مع تجربة منظمة ودعم ذكي.'}
                  </p>
                  <div className="credit-meter">
                    <div className="credit-meta">
                      <div>
                        <strong>رصيد الذكاء الاصطناعي</strong>
                        <div className="credit-number">{user?.ai_credits ?? 0}</div>
                        <div className="credit-support">متاح الآن للكتابة والتحدث والاختبارات المتقدمة.</div>
                      </div>
                      <span>{creditProgress}% متبقٍ</span>
                    </div>
                    <div className={`credit-status${lowCredits ? ' low' : ''}`}>
                      {lowCredits ? 'الرصيد منخفض — يُفضّل شحنه قريباً.' : 'الرصيد الحالي مناسب للإجراءات الذكية القادمة.'}
                    </div>
                    <div className="progress-track">
                      <div className={`progress-fill${lowCredits ? ' low' : ''}`} style={{ width: progressReady ? `${creditProgress}%` : '0%' }} />
                    </div>
                  </div>
                </div>

                <div className="upgrade-card">
                  <div className="eyebrow" style={{ color: 'rgba(255,255,255,.5)' }}>الخطوة الأنسب الآن</div>
                  <h3>{nextAction.title}</h3>
                  <p>{nextAction.copy}</p>
                  <ul className="upgrade-list">
                    <li><span className="upgrade-dot" /><span>{attempts.length > 0 ? `تم تسجيل ${attempts.length} اختبار${attempts.length > 1 ? 'ات' : ''} مكتمل في سجلّك.` : 'ستظهر النتائج هنا فور إكمالك أول اختبار.'}</span></li>
                    <li><span className="upgrade-dot" /><span>{totalExamsCount > 0 ? `يوجد الآن ${totalExamsCount} اختبار متاح يشمل الكتابة والتحدث والقراءة.` : 'لا توجد اختبارات منشورة حالياً ويمكن للمشرف إضافتها من لوحة الإدارة.'}</span></li>
                    <li><span className="upgrade-dot" /><span>{user?.plan === 'ai' ? 'استخدم وضع الاختبار الكامل للحصول على أقرب تجربة واقعية ممكنة.' : 'قم بالترقية عندما تحتاج إلى تقييم ذكي أو Speaking أو رصيد إضافي.'}</span></li>
                  </ul>
                  <button className="primary-cta" style={{ marginTop: 18, width: '100%' }} onClick={nextAction.onClick}>
                    {nextAction.cta}
                  </button>
                </div>
              </div>
            </section>
          </main>
      </div>
    </div>
  );
}
